import { useCallback, useEffect, useMemo, useState } from 'react';
import DiscardConfirmation, {
  useDiscardConfirmation,
} from 'components/DiscardConfirmation';
import { fetchAuthConfigs } from 'slices/authConfig.slice';
import { fetchFields, fetchFieldsGlobalOrder } from 'slices/field.slice';
import { fetchDateFormats, fetchLanguages } from 'slices/language.slice';
import { requestObjectTypes } from 'slices/objectTypes.slice';
import { fetchOrganizationHierarchy } from 'slices/organizationHierarchy.slice';
import { fetchPermissions } from 'slices/permission.slice';
import { fetchRoles } from 'slices/roles.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { fetchTimeZones } from 'slices/timeZone.slice';
import {
  checkUserSaveDisabled,
  selectedUsersSelector,
  setSelectedUsers,
} from 'slices/user.slice';
import { message } from 'utils/messageUtils';
import type { TTEUser } from '@timeedit/types';
import { DefaultContentLayout } from '@timeedit/ui-components';
import { Button } from '@timeedit/ui-components/antd';
import { UserDrawer } from './UserDrawer';
import UserTable from './UserTable';
import ModalImport from './importUsers/ModalImport';
import UserTemplate from './importUsers/UserTemplate';

const UserPage = () => {
  const dispatch = useAppDispatch();
  const [openUserTemplate, setOpenUserTemplate] = useState(false);
  const [openUserImport, setOpenUserImport] = useState(false);
  const selectedUsers = useAppSelector(selectedUsersSelector);

  const [
    openDiscardDialog,
    onCloseDiscardDialog,
    confirmFn,
    confirmDiscardChanged,
  ] = useDiscardConfirmation();

  useEffect(() => {
    dispatch(fetchAuthConfigs());
    dispatch(fetchLanguages());
    dispatch(fetchPermissions());
    dispatch(fetchOrganizationHierarchy());
    dispatch(fetchRoles());
    dispatch(requestObjectTypes());
    dispatch(fetchFields());
    dispatch(fetchTimeZones());
    dispatch(fetchDateFormats());
    dispatch(fetchFieldsGlobalOrder());
  }, [dispatch]);

  const confirmDiscardUserChanges = useCallback(
    (isFirstDrawer: boolean, actionFn: () => void) => {
      const isUserSaveDisabled = dispatch(checkUserSaveDisabled(isFirstDrawer));

      if (!isUserSaveDisabled) {
        return confirmDiscardChanged(() => {
          actionFn();
        });
      }
      actionFn();
    },
    [confirmDiscardChanged, dispatch]
  );

  const onRowSelect = useCallback(
    (users: TTEUser[]) => {
      dispatch(setSelectedUsers(users));
    },
    [dispatch]
  );

  const buttonActions = useMemo(() => {
    return [
      <Button
        key="export_users"
        onClick={() => {
          if (!selectedUsers.length)
            return message.info('Please select at least one user.');
          setOpenUserTemplate(true);
        }}
      >
        Export
      </Button>,
      <Button
        key="import_users"
        type="primary"
        onClick={() => setOpenUserImport(true)}
      >
        Import
      </Button>,
    ];
  }, [selectedUsers.length]);

  return (
    <DefaultContentLayout
      title="Users"
      subtitle="Create, edit or delete users."
      actions={buttonActions}
    >
      <div
        className="inner-content--wrapper te-flex"
        style={{ overflow: 'hidden' }}
      >
        <UserTable
          onRowSelect={onRowSelect}
          confirmDiscardUserChanges={confirmDiscardUserChanges}
        />
        <UserDrawer confirmDiscardUserChanges={confirmDiscardUserChanges} />
        <DiscardConfirmation
          open={openDiscardDialog}
          onClose={onCloseDiscardDialog}
          confirmFn={confirmFn}
        />
        <UserTemplate
          open={openUserTemplate}
          onClose={() => setOpenUserTemplate(false)}
        />
        <ModalImport
          isModalOpen={openUserImport}
          setIsModalOpen={setOpenUserImport}
        />
      </div>
    </DefaultContentLayout>
  );
};

export default UserPage;
